import { useState, useEffect, useCallback, useRef } from "react";


const SESSION_KEY = "ogm_user_location";
const PERM_KEY    = "ogm_location_granted";
const CACHE_TTL   = 10 * 60 * 1000;
const PERM_TTL    = 30 * 60 * 1000;
const LOG_PREFIX  = "[OGM Location]";

const NEAR_ME_PATTERNS = [
  /near\s+me/i,
  /near\s+by/i,
  /nearby/i,

  /my\s+(current\s+)?loc\w*/i,
  /what\s+is\s+my\s+loc\w*/i,
  /where\s+am\s+i/i,

  /within\s+\d+\s*km\s+(from\s+(me|here|my)|near\s+me|of\s+me)/i,

  /\d+\s*km\s+(from\s+(me|here|my\s+\w*)|near\s+me|around\s+me)/i,

  /from\s+here/i,
  /close\s+to\s+me/i,
  /around\s+me/i,
  /properties\s+(here|around|close)/i,
  /find\s+.*(here|this\s+area)/i,
  /what('s|\s+is)\s+(around|nearby|near\s+me)/i,
  /show\s+.*(near(by)?|around\s+me)/i,
];

export function detectNearMeIntent(text) {
  if (!text) return false;
  const result = NEAR_ME_PATTERNS.some((p) => p.test(text));
  if (result) console.debug(LOG_PREFIX, "Near-me intent detected for:", text);
  return result;
}


export async function reverseGeocode(latitude, longitude) {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  if (!apiKey) {
    console.warn(LOG_PREFIX, "REACT_APP_GOOGLE_MAPS_API_KEY not set — location name unavailable.");
    return null;
  }
  try {
    const url =
        `https://maps.googleapis.com/maps/api/geocode/json` +
        `?latlng=${latitude},${longitude}` +
        `&key=${apiKey}` +
        `&result_type=sublocality|locality|administrative_area_level_2`;

    const res = await fetch(url);
    if (!res.ok) { console.warn(LOG_PREFIX, "Geocode HTTP error:", res.status); return null; }

    const data = await res.json();
    if (data.status !== "OK" || !data.results?.length) {
      console.warn(LOG_PREFIX, "Geocoding status:", data.status);
      return null;
    }

    const components = data.results[0].address_components || [];
    const get = (type) => components.find((c) => c.types.includes(type))?.long_name ?? null;

    const sublocality = get("sublocality_level_1") || get("sublocality") || get("neighborhood");
    const city        = get("locality") || get("administrative_area_level_2");
    const state       = get("administrative_area_level_1");
    const country     = get("country");

    const name = [sublocality, city, state, country].filter(Boolean).join(", ")
        || data.results[0].formatted_address;

    console.info(LOG_PREFIX, "Reverse geocoded to:", name);
    return name;
  } catch (err) {
    console.error(LOG_PREFIX, "Reverse geocoding failed:", err.message);
    return null;
  }
}


export function haversineDistance(lat1, lng1, lat2, lng2) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null;
  const R    = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function formatDistance(km) {
  if (km == null) return null;
  if (km < 1)   return `${Math.round(km * 1000)} m away`;
  if (km < 10)  return `${km.toFixed(1)} km away`;
  return `${Math.round(km)} km away`;
}


export default function useGeolocation() {
  const [position,         setPosition]         = useState(null);
  const [locationName,     setLocationName]     = useState(null);
  const [permissionStatus, setPermissionStatus] = useState("unknown");
  const [isRequesting,     setIsRequesting]     = useState(false);
  const [error,            setError]            = useState(null);
  const watchIdRef = useRef(null);

  useEffect(() => {
    let shouldRefreshGPS = false;

    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) {
        const { data, ts } = JSON.parse(raw);
        if (Date.now() - ts < CACHE_TTL && data?.latitude && data?.longitude) {
          console.info(LOG_PREFIX, "Restored from sessionStorage cache:", data.locationName || "no name");
          setPosition(data);
          if (data.locationName) setLocationName(data.locationName);
          setPermissionStatus("granted");
          if (Date.now() - ts > 5 * 60 * 1000) shouldRefreshGPS = true;
        } else {
          sessionStorage.removeItem(SESSION_KEY);
          shouldRefreshGPS = true;
        }
      }
    } catch { /* ignore */ }

    try {
      const raw = localStorage.getItem(PERM_KEY);
      if (raw) {
        const pos = JSON.parse(raw);
        const cacheAge = pos?.ts ? (Date.now() - pos.ts) : Infinity;
        if (pos?.latitude && pos?.longitude && cacheAge < PERM_TTL) {
          if (!position) {
            console.info(LOG_PREFIX, "Restored from localStorage grant:", pos.locationName || "no name");
            const data = { ...pos, accuracy: null };
            setPosition(data);
            if (pos.locationName) setLocationName(pos.locationName);
            setPermissionStatus("granted");
            try {
              sessionStorage.setItem(SESSION_KEY, JSON.stringify({ data, ts: pos.ts || Date.now() }));
            } catch { /* ignore */ }
          }
        } else if (pos?.latitude && pos?.longitude) {
          console.info(LOG_PREFIX, "localStorage cache expired (age:", Math.round(cacheAge / 60000), "min) — will refresh GPS");
          if (!position) {
            const data = { ...pos, accuracy: null };
            setPosition(data);
            if (pos.locationName) setLocationName(pos.locationName);
            setPermissionStatus("granted");
          }
          shouldRefreshGPS = true;
        }
      }
    } catch { /* ignore */ }

    if ("permissions" in navigator) {
      navigator.permissions
          .query({ name: "geolocation" })
          .then((result) => {
            console.debug(LOG_PREFIX, "Browser permission state:", result.state);
            if (result.state !== "granted") {
              setPermissionStatus(result.state);
            }
            if (result.state === "granted") {
              shouldRefreshGPS = true;
            }
            result.onchange = () => {
              console.info(LOG_PREFIX, "Permission changed to:", result.state);
              setPermissionStatus(result.state);
              if (result.state === "denied") {
                try { localStorage.removeItem(PERM_KEY); } catch { /* ignore */ }
                try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
                setPosition(null);
                setLocationName(null);
              }
            };

            if (shouldRefreshGPS && result.state === "granted") {
              silentRefresh();
            }
          })
          .catch(() => {
            if (shouldRefreshGPS) silentRefresh();
          });
    } else if (shouldRefreshGPS) {
      silentRefresh();
    }

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  const silentRefresh = useCallback(() => {
    if (!("geolocation" in navigator)) return;

    console.info(LOG_PREFIX, "Silent GPS refresh — updating location in background...");

    navigator.geolocation.getCurrentPosition(
        async (pos) => {
          console.info(LOG_PREFIX, "Silent refresh obtained — accuracy:",
              pos.coords.accuracy?.toFixed(0), "m");

          const data = {
            latitude:  pos.coords.latitude,
            longitude: pos.coords.longitude,
            accuracy:  pos.coords.accuracy,
          };

          const name = await reverseGeocode(data.latitude, data.longitude);
          const dataWithName = { ...data, locationName: name };

          setPosition(dataWithName);
          setLocationName(name);
          setPermissionStatus("granted");

          const now = Date.now();
          try {
            sessionStorage.setItem(SESSION_KEY, JSON.stringify({ data: dataWithName, ts: now }));
          } catch { /* storage full */ }
          try {
            localStorage.setItem(PERM_KEY, JSON.stringify({ ...dataWithName, ts: now }));
          } catch { /* storage full */ }

          console.info(LOG_PREFIX, "Silent refresh complete:",
              name || `${data.latitude.toFixed(4)}, ${data.longitude.toFixed(4)}`);
        },
        (err) => {
          console.debug(LOG_PREFIX, "Silent refresh failed (code", err.code, ") — using cached position");
        },
        {
          enableHighAccuracy: true,
          timeout:    10000,
          maximumAge: 60000,
        }
    );
  }, []);

  const requestLocation = useCallback((forceRefresh = false) => {
    if (!("geolocation" in navigator)) {
      const msg = "Your browser does not support geolocation.";
      console.error(LOG_PREFIX, msg);
      setError(msg);
      setPermissionStatus("denied");
      return Promise.reject(new Error("Geolocation not supported"));
    }

    setIsRequesting(true);
    setError(null);
    console.info(LOG_PREFIX, "Requesting GPS location from browser...", forceRefresh ? "(FORCED REFRESH)" : "");

    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
          async (pos) => {
            console.info(LOG_PREFIX, "GPS obtained — accuracy:", pos.coords.accuracy?.toFixed(0), "m");
            console.info(LOG_PREFIX, "GPS coordinates:", pos.coords.latitude.toFixed(6), pos.coords.longitude.toFixed(6));
            console.info(LOG_PREFIX, "GPS source:", pos.coords.accuracy < 100 ? "Device GPS (High Accuracy)" : "Network/WiFi (Low Accuracy)");

            const data = {
              latitude:  pos.coords.latitude,
              longitude: pos.coords.longitude,
              accuracy:  pos.coords.accuracy,
            };

            const name = await reverseGeocode(data.latitude, data.longitude);
            const dataWithName = { ...data, locationName: name };

            setPosition(dataWithName);
            setLocationName(name);
            setPermissionStatus("granted");
            setIsRequesting(false);

            try {
              sessionStorage.setItem(SESSION_KEY, JSON.stringify({ data: dataWithName, ts: Date.now() }));
            } catch { /* storage full */ }

            try {
              localStorage.setItem(PERM_KEY, JSON.stringify({ ...dataWithName, ts: Date.now() }));
            } catch { /* storage full */ }

            console.info(LOG_PREFIX, "Location ready:", name || `${data.latitude.toFixed(4)}, ${data.longitude.toFixed(4)}`);
            resolve(dataWithName);
          },
          (err) => {
            setIsRequesting(false);
            let msg;
            switch (err.code) {
              case 1:
                msg = "Location access was denied. Please enable it in browser settings.";
                setPermissionStatus("denied");
                try { localStorage.removeItem(PERM_KEY); } catch { /* ignore */ }
                break;
              case 2:
                msg = "Unable to determine your location. Check GPS signal and try again.";
                break;
              case 3:
                msg = "Location request timed out. Please try again.";
                break;
              default:
                msg = "Location request failed. Please try again.";
            }
            console.warn(LOG_PREFIX, "GPS error code", err.code, "—", msg);
            setError(msg);
            reject(err);
          },
          {
            enableHighAccuracy: true,
            timeout:    forceRefresh ? 30000 : 15000,
            maximumAge: forceRefresh ? 0 : CACHE_TTL,
          }
      );
    });
  }, []);

  const clearLocation = useCallback(() => {
    setPosition(null);
    setLocationName(null);
    setError(null);
    try { sessionStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    try { localStorage.removeItem(PERM_KEY); } catch { /* ignore */ }
    console.info(LOG_PREFIX, "Location cache cleared. Request location again to get fresh GPS.");
  }, []);

  const forceRefresh = useCallback(() => {
    console.info(LOG_PREFIX, "Force refreshing GPS location (clearing cache)...");
    clearLocation();
    return requestLocation(true);
  }, [clearLocation, requestLocation]);

  return {
    position,
    locationName,
    permissionStatus,
    isRequesting,
    error,
    requestLocation,
    clearLocation,
    forceRefresh,
    isGranted: permissionStatus === "granted",
    isDenied:  permissionStatus === "denied",
  };
}
